import {BreakpointObserver, MediaMatcher} from '@angular/cdk/layout';
import {Component, OnInit, ViewChild, ViewEncapsulation, inject, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {MatSidenav, MatSidenavContent} from '@angular/material/sidenav';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router, RouterOutlet, RouterModule} from '@angular/router';
import {MaterialModule} from '@app/material.module';
import {CommonModule} from '@angular/common';
import {NgScrollbarModule} from 'ngx-scrollbar';
import {TablerIconsModule} from 'angular-tabler-icons';
import {AppNavItemComponent} from '@layout/full-layout/vertical/sidebar/nav-item/nav-item.component';
import {HeaderComponent} from '@layout/full-layout/vertical/header/header.component';
import {AppHorizontalHeaderComponent} from '@layout/full-layout/horizontal/header/header.component';
import {AppHorizontalSidebarComponent} from '@layout/full-layout/horizontal/sidebar/sidebar.component';
import {NavService} from '@services/nav.service';
import {SidebarComponent} from '@layout/full-layout/vertical/sidebar/sidebar.component';
import {BrandingComponent} from '@layout/full-layout/branding/branding.component';
import {AppBreadcrumbComponent} from '@layout/full-layout/shared/breadcrumb/breadcrumb.component';
import {quicklinks} from '@interfaces/quicklinks'
import {CoreService} from '@services/core.service';
import {AppSettings} from '@app/config';
import {navItemsSuperadmin} from '@core/data/sidebar/sidebar-superadmin-data';
import {apps} from '@interfaces/apps';
import { FilemanagerPinnableStateService } from '@app/core/services/filemanager-pinnable-state/filemanager-pinnable-state.service';
import { SidenarStateService } from '@app/core/services/sidebar-state/sidenar-state.service';
import { UserSettingsService } from '@app/core/services/user_settings/user-settings.service';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

@Component({
  selector: 'app-full-layout',
  imports: [RouterOutlet, RouterModule, AppNavItemComponent, MaterialModule, CommonModule, SidebarComponent,
    NgScrollbarModule, TablerIconsModule, HeaderComponent, AppHorizontalHeaderComponent, AppHorizontalSidebarComponent,
    AppBreadcrumbComponent, BrandingComponent,],
  standalone: true,
  templateUrl: './full-layout.component.html',
  styleUrl: './full-layout.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FullLayoutComponent {

  @ViewChild('leftsidenav')
  public sidenav!: MatSidenav;
  resView = false;
  selectedIcon = 1;
  navItems = navItemsSuperadmin;

  @ViewChild('content', {static: true}) content!: MatSidenavContent;
  //get options from service
  private settings = inject(CoreService);
  private userSettingsService = inject(UserSettingsService);
  private initialUserSettings:any;
  // private mediaMatcher = inject(MediaMatcher);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private navService = inject(NavService);

  private filemanagerPinnableStateService = inject(FilemanagerPinnableStateService);
  private isPinnedState = signal<boolean>(false);

  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  private isMouseOverSidenav = false;

// Track if we should keep the sidenav expanded
private keepExpanded = false;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  // for mobile app sidebar
  apps: apps[] = [];
  quicklinks: quicklinks[] = [];

  onIconSelected(icon: number) {
    this.selectedIcon = icon;
  }

  constructor() {
    this.initialUserSettings = this.userSettingsService.getState();
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state: any) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.content.scrollTo({top: 0});
      });
  }

  ngOnInit(): void {
    this.setUserSettingsInitialState();
    this.initFilemanaderSidebarState();
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  openClosedMenu() {
    this.options.sidenavCollapsed = false;
    this.resetCollapsedState();
  }


  onMouseEnter() {
    this.isMouseOverSidenav = true;
    if (this.options.sidenavCollapsed && !this.isMobileScreen && !this.isTablet) {
      this.options.sidenavCollapsed = false;
      this.resetCollapsedState();
    }
  }

  onMouseLeave() {
    this.isMouseOverSidenav = false;
    if(this.keepExpanded){
      return;
    }
    if(!this.keepExpanded && !this.isMobileScreen && !this.isTablet){
      this.options.sidenavCollapsed = true;
      this.resetCollapsedState();
    }
  }

  toggleCollapsed() {
    // if(this.isPinnedState()){
    //   return;
    // }
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.userSettingsService.setState(this.options.sidenavCollapsed, 'sidenavCollapsed');
    this.keepExpanded = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleColorsTheme(options: AppSettings) {
    // Remove any existing theme class dynamically
    this.htmlElement.classList.forEach((className) => {
      if (className.endsWith('_theme')) {
        this.htmlElement.classList.remove(className);
      }
    });

    // Add the selected theme class
    this.htmlElement.classList.add(options.activeTheme);
  }

  initFilemanaderSidebarState() {
    this.filemanagerPinnableStateService.filemanagerPinnedState$.subscribe(state => {
      this.isContentWidthFixed = false;
      this.options.sidenavCollapsed = state;
      this.resetCollapsedState();
      this.isPinnedState.set(state);
    });
  }

  setUserSettingsInitialState() {
    if (this.initialUserSettings) {
      this.isContentWidthFixed = false;
      this.options.sidenavCollapsed = this.initialUserSettings?.sidenavCollapsed;
      this.keepExpanded = !this.initialUserSettings?.sidenavCollapsed;
      this.isPinnedState.set(this.initialUserSettings?.modalpinned);
      this.resetCollapsedState();
      // this.options.sidenavOpened = this.initialUserSettings?.sidenavOpened || true;
    }
  }


}
